import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenuSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { UserIcon } from "@libs/zapi-ui/assets";
import {
  getRouteCookieNotice,
  getRoutePrivacyNotice,
  getRouteSettings,
  getRouteTermsOfUse
} from "@/zapi-frontend/shared/const/router";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasAdminPrivileges, isSystemAdmin } from "@/zapi-frontend/shared/utils/permissions";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { postLogout } from "../model/services";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { CookieIcon, FileLockIcon, ShieldCheckIcon, SignOutIcon, BuildingIcon } from "@phosphor-icons/react";
import { SmallModeSwitch } from "@/zapi-frontend/features/LabsModeSwitch";
import { useOrganizationStore } from "@/zapi-frontend/stores/organization";
import { useFeatureFlag } from "@/zapi-frontend/shared/hooks/useFeatureFlag";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const menuItemClasses = "cursor-pointer font-medium px-3 py-2";

export function useSignOut() {
  const { t } = useTranslation("common");
  return async () => {
    const [, error] = await tryCatch(postLogout());
    if (error) {
      toast.error(t("userMenu.signOutError"));
      return;
    }
    useOrganizationStore.getState().reset();
    window.location.reload();
  };
}

export function UserMenu() {
  const { t } = useTranslation("common");
  const { role, firstName, lastName, profileImageUrl, organizationLogoUrl } = useMeStore();
  const labsModeAvailable = useFeatureFlag("labs-mode");
  const isLabsSwitchVisible = labsModeAvailable || isSystemAdmin(role);
  const navigate = useNavigate();
  const signOutHandler = useSignOut();

  const organizationLogo = useMemo(() => {
    if (organizationLogoUrl)
      return (
        <img
          src={organizationLogoUrl}
          alt="organization logo"
          className="absolute -bottom-1 -right-1 h-4 w-4 bg-innoveraZinc-950 rounded-[5px] p-0.5 border-2 border-white object-cover"
        />
      );

    return (
      <BuildingIcon className="absolute -bottom-1 -right-1 h-4 w-4 text-white bg-innoveraZinc-950 rounded-[5px] p-0.5 border-2 border-white" />
    );
  }, [organizationLogoUrl]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="user-menu-trigger">
        {profileImageUrl && (
          <div className="relative flex items-center justify-center">
            <Avatar>
              <AvatarImage src={profileImageUrl} />
              <AvatarFallback className="bg-zinc-700">
                <UserIcon className="h-4 w-4 text-zinc-500" />
              </AvatarFallback>
            </Avatar>
            {organizationLogo}
          </div>
        )}
        {!profileImageUrl && (
          <div className="relative flex items-center justify-center">
            <Avatar>
              <AvatarFallback className="bg-zinc-700">
                <FigmaTypography variant="body-2" className="text-innoveraGeneral-white">
                  {firstName[0]}
                  {lastName[0]}
                </FigmaTypography>
              </AvatarFallback>
            </Avatar>
            {organizationLogo}
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[270px] px-1 mr-5">
        {isLabsSwitchVisible && (
          <>
            <SmallModeSwitch id="header-labs-mode" className="p-3" />
            <DropdownMenuSeparator className="mx-2 bg-innoveraZinc-150" />
          </>
        )}
        <DropdownMenuItem
          data-testid="user-menu-profile"
          variant="default"
          onClick={() => navigate(getRouteSettings() + "?tab=Profile")}
          className={menuItemClasses}
        >
          <UserIcon className="w-4 h-4 text-black" />
          <FigmaTypography variant="label-2">{t("userMenu.profile")}</FigmaTypography>
        </DropdownMenuItem>
        {hasAdminPrivileges(role) && (
          <DropdownMenuItem
            data-testid="user-menu-organization"
            onClick={() => navigate(getRouteSettings() + "?tab=Team")}
            className={menuItemClasses}
          >
            <BuildingIcon className="w-4 h-4 text-black" />
            <FigmaTypography variant="label-2">{t("userMenu.organization")}</FigmaTypography>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="mx-2 bg-innoveraZinc-150" />
        <DropdownMenuItem
          data-testid="user-menu-privacy-notice-item"
          onClick={() => navigate(getRoutePrivacyNotice())}
          className={menuItemClasses}
        >
          <ShieldCheckIcon className="w-4 h-4 text-black" />
          <FigmaTypography variant="label-2">{t("userMenu.privacyNotice")}</FigmaTypography>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-terms-of-use-item"
          onClick={() => navigate(getRouteTermsOfUse())}
          className={menuItemClasses}
        >
          <FileLockIcon className="w-4 h-4 text-black" />
          <FigmaTypography variant="label-2">{t("userMenu.termsOfUse")}</FigmaTypography>
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid="user-menu-cookie-notice-item"
          onClick={() => navigate(getRouteCookieNotice())}
          className={menuItemClasses}
        >
          <CookieIcon className="w-4 h-4 text-black" />
          <FigmaTypography variant="label-2">{t("userMenu.cookieNotice")}</FigmaTypography>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-2 bg-innoveraZinc-150" />

        <DropdownMenuItem
          data-testid="user-menu-signout"
          onClick={signOutHandler}
          className="cursor-pointer font-medium text-zinc-700 px-3 py-2"
        >
          <SignOutIcon className="w-4 h-4 text-black" />
          <FigmaTypography variant="label-2">{t("userMenu.signOut")}</FigmaTypography>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
